import { Box, Chip, Grid, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from '@pruforce/common-adminweb-sdk';
import { Alert } from '@mui/material';

import { PreviewField } from './preview-field';
import { PreviewSection } from './preview-section';
import { useStyles } from './preview-confirm.step.style';
import { AdminRegistrationFormData } from '../../admin-registration.dialog';
import { useMemo, useState } from 'react';
import { GENDER_OPTIONS } from '../registration-information/registration-information.hook';
import { useSelector } from 'react-redux';
import { RegionIdType } from 'src/app/common/types';
import { selectExamModeMap, selectRegionIdTypes } from 'src/app/modules/Config/_redux/configSlice';
import { ErrorOutline } from '@mui/icons-material';
interface PreviewConfirmStepProps {
  formData: AdminRegistrationFormData;
  confirmError?: string;
  onDismissError?: () => void;
}

export const PreviewConfirmStep = ({ formData, confirmError, onDismissError }: PreviewConfirmStepProps) => {
  const { registrationInformation, selectedExamPaper, selectedVenue, selectedSession } = formData;
  const t = useTranslation();
  const { classes } = useStyles();
  const [dismissedZeroQuotaSessionId, setDismissedZeroQuotaSessionId] = useState<string | null>(null);
  const showZeroQuotaWarning =
    selectedSession?.remainingQuota === 0 && selectedSession.value !== dismissedZeroQuotaSessionId;
  const examModeMap: Record<string, string> = useSelector(selectExamModeMap);
  const idTypeOptions: RegionIdType[] = useSelector(selectRegionIdTypes);

  const noChineseName = useMemo(() => {
    return registrationInformation.noChineseName ? t('global.text.yes') : t('global.text.no')
  }, [registrationInformation.noChineseName, t])

  const idType = useMemo(() => {
    const typeOption = idTypeOptions.find(({ value }) => value === registrationInformation.identityDocumentType);
    return typeOption ? t(typeOption.label) : '';
  }, [registrationInformation.identityDocumentType, idTypeOptions, t])

  const gender = useMemo(() => {
    const genderOption = GENDER_OPTIONS.find(({ value }) => value === registrationInformation.gender);
    return genderOption ? t(genderOption.label) : '';
  }, [registrationInformation.gender, t])

  return (
    <div className={classes.container}>
      {showZeroQuotaWarning && (
        <Alert
          severity="warning"
          onClose={() => setDismissedZeroQuotaSessionId(selectedSession?.value ?? null)}
          className={classes.warningAlert}
        >
          {t('recruitment.exam.registration.adminRegistration.registrationInformation.zeroQuotaWarning')}
        </Alert>
      )}
      {confirmError && (
        <Alert
          severity="warning"
          icon={<ErrorOutline />}
          onClose={onDismissError}
          className={classes.warningAlert}
        >
          {confirmError}
        </Alert>
      )
      }
      <Grid container spacing={5}>
        {/* Left */}
        <Grid item xs={7}>
          <Stack spacing={2.5}>
            <PreviewSection title={t('recruitment.exam.registration.adminRegistration.registrationConfirmation.section1Title')}>
              <Grid container columnSpacing={3} rowSpacing={1}>
                <Grid item xs={6}>
                  <PreviewField label={t("recruitment.exam.registration.firstName")}>
                    {registrationInformation.firstname}
                  </PreviewField>
                </Grid>

                <Grid item xs={6}>
                  <PreviewField label={t("recruitment.exam.registration.lastName")}>
                    {registrationInformation.lastname}
                  </PreviewField>
                </Grid>

                <Grid item xs={6}>
                  <PreviewField label={t("localName")}>
                    {registrationInformation.localName}
                  </PreviewField>
                </Grid>

                <Grid item xs={6}>
                  <PreviewField label={t("recruitment.exam.registration.adminRegistration.registrationInformation.noChineseName")}>
                    {noChineseName}
                  </PreviewField>
                </Grid>

                <Grid item xs={6}>
                  <PreviewField label={t("recruitment.exam.registration.adminRegistration.registrationInformation.identificationType")}>
                    {idType}
                  </PreviewField>
                </Grid>

                <Grid item xs={6}>
                  <PreviewField label={t("identityDocumentNumber")}>
                    {registrationInformation.identityDocumentNumber}
                  </PreviewField>
                </Grid>

                <Grid item xs={6}>
                  <PreviewField label={t("gender")}>
                    {gender}
                  </PreviewField>
                </Grid>

                <Grid item xs={6}>
                  <PreviewField label={t("birthday")}>
                    {registrationInformation.birthday}
                  </PreviewField>
                </Grid>
              </Grid>
            </PreviewSection>

            <PreviewSection title={t("recruitment.exam.registration.adminRegistration.registrationInformation.section3Title")}>
              <Grid container columnSpacing={3} rowSpacing={1}>
                <Grid item xs={6}>
                  <PreviewField label={t("recruitment.exam.registration.adminRegistration.registrationInformation.mobileNumber")}>
                    {`${registrationInformation.countryCode} ${registrationInformation.phoneNumber}`}
                  </PreviewField>
                </Grid>

                <Grid item xs={6}>
                  <PreviewField label={t("recruitment.exam.registration.adminRegistration.registrationInformation.emailAddress")}>
                    {registrationInformation.email}
                  </PreviewField>
                </Grid>
              </Grid>
            </PreviewSection>

            <PreviewSection
              title={t("recruitment.exam.registration.adminRegistration.registrationInformation.section4Title")}
              headerClassName={classes.fileSectionHeader}
            >
              <Grid container columnSpacing={1} rowSpacing={1}>
                {registrationInformation.evidenceFiles.length === 0 ? (
                  <Grid item xs={6}>
                    <Typography variant="body2" className={classes.noFileUploaded}>
                      {t("recruitment.exam.registration.noFileUploaded")}
                    </Typography>
                  </Grid>
                ) : (
                  registrationInformation.evidenceFiles.map((file) => (
                    <Grid item xs={6} key={file.id} >
                      <Box className={classes.fileItem}>
                        <Tooltip key={file.id} title={file.name}>
                          <Typography
                            className={classes.fieldValue}
                            variant="body1"
                            noWrap>
                            {file.name}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Grid>
                  ))
                )}
              </Grid>
            </PreviewSection>
          </Stack>
        </Grid>

        {/* Right */}
        <Grid item xs={5}>
          <PreviewSection
            title={t("recruitment.exam.registration.adminRegistration.registrationConfirmation.examInformation")}
            className={classes.examSection}
            headerClassName={classes.examSectionHeader}
            titleClassName={classes.examSectionTitle}
          >
            <Stack spacing={1} className={classes.examContent}>
              <PreviewField label={t("recruitment.exam.registration.adminRegistration.registrationConfirmation.paperName")}>
                {selectedExamPaper?.localized}
              </PreviewField>
              <PreviewField label={t("recruitment.exam.registration.adminRegistration.registrationConfirmation.venueName")}>
                {selectedVenue}
              </PreviewField>
              <PreviewField label={t("recruitment.exam.registration.adminRegistration.registrationConfirmation.examDate")}>
                {selectedSession?.dateText}
              </PreviewField>
              <PreviewField label={t("recruitment.exam.registration.adminRegistration.registrationConfirmation.examSession")}>
                {selectedSession?.timeText}
              </PreviewField>
              <PreviewField label={t("recruitment.exam.registration.adminRegistration.registrationConfirmation.examMode")}>
                {selectedSession?.examMode ? t(examModeMap[selectedSession?.examMode]) : selectedSession?.examMode}
              </PreviewField>
              <PreviewField
                label={t("recruitment.exam.registration.adminRegistration.registrationConfirmation.remainingQuota")}
                divider={false}
              >
                <Stack mt={0.5} direction="row" alignItems="center" spacing={1}>
                  <Chip className={classes.remainingQuota} label={`${selectedSession?.remainingQuota} / ${selectedSession?.quota}`} />
                  {selectedSession?.remainingQuota === 0 && (
                    <Typography className={classes.remainingQuotaDesc} variant="body2" noWrap>
                      {t("recruitment.exam.registration.adminRegistration.registrationConfirmation.remainingQuotaHint")}
                    </Typography>
                  )}
                </Stack>
              </PreviewField>
            </Stack>
          </PreviewSection>
        </Grid>
      </Grid>
    </div >
  );
};